import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { OutfitDraftProvider } from "@/context/OutfitDraftContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ToastProvider } from "@/components/Toast";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { initSentry } from "@/lib/sentry";
import { trackScreen, track } from "@/lib/analytics";

// Activate Sentry only if REACT_APP_SENTRY_DSN is set. No-op otherwise.
initSentry();

// Map route paths → feature key. Used to fire `feature_opened` centrally
// so we don't have to edit every screen. Longest-prefix match wins.
const PATH_TO_FEATURE = [
  ["/improve/fitness",       "fitness"],
  ["/improve/body-goal",     "fitness"],
  ["/improve/skincare",      "skincare"],
  ["/improve/fragrance",     "fragrance"],
  ["/improve/grooming/hair", "grooming"],
  ["/improve/grooming/beard","grooming"],
  ["/improve/grooming",      "grooming"],
  ["/improve/motivate",      "coach"],
  ["/coach",                 "coach"],
  ["/nutrition-scan",        "nutrition"],
  ["/nutrition",             "nutrition"],
  ["/rate-my-outfit",        "style"],
  ["/style",                 "style"],
  ["/progress",              "progress"],
];

function featureForPath(path) {
  if (!path) return null;
  for (const [prefix, feat] of PATH_TO_FEATURE) {
    if (path === prefix || path.startsWith(prefix + "/")) return feat;
  }
  return null;
}

// Fires screen_viewed on every route change. Query strings are stripped
// inside trackScreen(), so tokens/emails in URLs never leave the device.
// Also fires `feature_opened` when the route belongs to a tracked feature.
function RouteChangeTracker() {
  const location = useLocation();
  const [prev, setPrev] = useState(null);
  useEffect(() => {
    try {
      trackScreen(location.pathname, prev);
      const feat = featureForPath(location.pathname);
      if (feat) track("feature_opened", { feature: feat });
    } catch { /* ignore */ }
    setPrev(location.pathname);
  }, [location.pathname]);
  return null;
}

import WelcomeScreen from "@/screens/WelcomeScreen";
import HomeScreen from "@/screens/HomeScreen";
import UploadScreen from "@/screens/UploadScreen";
import StyleScreen from "@/screens/StyleScreen";
import StylistLandingScreen from "@/screens/StylistLandingScreen";
import StyleHubScreen from "@/screens/StyleHubScreen";
import ImproveScreen from "@/screens/ImproveScreen";
import FitnessCoachScreen from "@/screens/FitnessCoachScreen";
import BodyGoalCoachScreen from "@/screens/BodyGoalCoachScreen";
import ForgotPasswordScreen from "@/screens/ForgotPasswordScreen";
import ResetPasswordScreen from "@/screens/ResetPasswordScreen";
import VerifyEmailScreen from "@/screens/VerifyEmailScreen";
import VerifyEmailChangeScreen from "@/screens/VerifyEmailChangeScreen";
import EditAccountScreen from "@/screens/EditAccountScreen";
import DeleteAccountRequestScreen from "@/screens/DeleteAccountRequestScreen";
import SupportScreen from "@/screens/SupportScreen";
import AdminUsersScreen from "@/screens/AdminUsersScreen";
import GroomingHubScreen from "@/screens/GroomingHubScreen";
import HairCoachScreen from "@/screens/HairCoachScreen";
import BeardCoachScreen from "@/screens/BeardCoachScreen";
import MotivationCoachScreen from "@/screens/MotivationCoachScreen";
import SkincareCoachScreen from "@/screens/SkincareCoachScreen";
import FragranceCoachScreen from "@/screens/FragranceCoachScreen";
import ProfileScreen from "@/screens/ProfileScreen";
import AccountScreen from "@/screens/AccountScreen";
import SettingsScreen from "@/screens/SettingsScreen";
import RemindersSettingsScreen from "@/screens/RemindersSettingsScreen";
import PrivacyPolicyScreen from "@/screens/PrivacyPolicyScreen";
import TermsScreen from "@/screens/TermsScreen";
import CommunityGuidelinesScreen from "@/screens/CommunityGuidelinesScreen";
import ResultsScreen from "@/screens/ResultsScreen";
import SavedScreen from "@/screens/SavedScreen";
import PremiumScreen from "@/screens/PremiumScreen";
import CelebrityScreen from "@/screens/CelebrityScreen";
import BudgetScreen from "@/screens/BudgetScreen";
import RateMyOutfitScreen from "@/screens/RateMyOutfitScreen";
import FindSimilarScreen from "@/screens/FindSimilarScreen";
import AppearanceScreen from "@/screens/AppearanceScreen";
import ProfileSetupScreen from "@/screens/ProfileSetupScreen";
import OnboardingScreen from "@/screens/OnboardingScreen";
import FashionFeedScreen from "@/screens/FashionFeedScreen";
import FashionFeedNewScreen from "@/screens/FashionFeedNewScreen";
import CreatorProfileScreen from "@/screens/CreatorProfileScreen";
import ProgressScreen from "@/screens/ProgressScreen";
import CoachScreen from "@/screens/CoachScreen";
import NutritionScreen from "@/screens/NutritionScreen";
import NutritionScannerScreen from "@/screens/NutritionScannerScreen";
import TrendsScreen from "@/screens/TrendsScreen";
import GlowUpAnalyzeScreen from "@/screens/GlowUpAnalyzeScreen";
import BottomNav from "@/components/BottomNav";

function LoadingShell() {
  return (
    <div className="screen" style={{ alignItems: "center", justifyContent: "center" }} data-testid="app-loading">
      <div className="spinner spinner-lg" />
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <LoadingShell />;
  if (user === false) return <Navigate to="/" replace />;
  return children;
}

// RequireOnboarding wraps every in-app screen and silently redirects the
// user to `/onboarding` until their 5 core fields (Name + Age + Gender +
// Height + Weight) are filled. This is a HARD gate — there's no way to
// reach the app without finishing onboarding (including bypassing via the
// URL bar). Once `/profile/onboarding-status.onboarded === true` the
// wrapper passes the route through unchanged.
//
// Performance: we cache the "onboarded" decision per-session in a module
// variable so we don't re-fetch on every route change. The wrapper
// invalidates the cache on logout (AuthContext flips user → false).
let _onboardedCacheUserId = null;
let _onboardedCacheValue  = null;

function RequireOnboarding({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState(() => {
    if (user && _onboardedCacheUserId === user.id) return _onboardedCacheValue;
    return null; // unknown — block render until we know
  });

  useEffect(() => {
    if (!user) {
      setStatus(null);
      _onboardedCacheUserId = null;
      _onboardedCacheValue = null;
      return;
    }
    if (_onboardedCacheUserId === user.id && _onboardedCacheValue !== null) {
      setStatus(_onboardedCacheValue);
      return;
    }
    let cancelled = false;
    import("@/lib/api").then(({ default: api }) =>
      api.get("/profile/onboarding-status").then((r) => {
        if (cancelled) return;
        const v = !!r.data?.onboarded;
        _onboardedCacheUserId = user.id;
        _onboardedCacheValue = v;
        setStatus(v);
      }).catch(() => {
        if (cancelled) return;
        // On network error, fail OPEN so the app is still usable. The
        // server-side prompts also read the live profile, so a missing
        // basic just means less personalisation — never a crash.
        setStatus(true);
      })
    );
    return () => { cancelled = true; };
  }, [user]);

  if (status === null) return <LoadingShell />;
  if (status === false && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  // Already onboarded — guard against the user re-visiting /onboarding by
  // accident (e.g. via bookmark). Send them straight to /home unless they
  // explicitly entered onboarding via the Profile screen's "edit" action.
  if (status === true && location.pathname === "/onboarding") {
    return <Navigate to="/home" replace />;
  }
  return children;
}

// Public helper: call after PUT /profile so the next RequireOnboarding
// render sees the fresh status without waiting for the cache to expire.
export function invalidateOnboardingCache() {
  _onboardedCacheUserId = null;
  _onboardedCacheValue = null;
}

// Combined gate for every authenticated route: must be logged in AND must
// have completed onboarding. Use this everywhere instead of bare <Protected>
// so adding a new route can't accidentally bypass the gate.
function Gated({ children }) {
  return (
    <Protected>
      <RequireOnboarding>{children}</RequireOnboarding>
    </Protected>
  );
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <LoadingShell />;
  if (user) return <Navigate to="/home" replace />;
  return children;
}

function Shell({ children, hasNav }) {
  return <div className={`phone-shell ${hasNav ? "has-nav" : ""}`}>{children}</div>;
}

// Bottom navigation is shown ONLY on authenticated, non-immersive screens.
// Hidden on: welcome/auth, full-screen Fashion Feed, compose screen, creator
// profile, loading, AND the chat-style Motivation Coach (so the composer can
// dock at the very bottom of the screen without the nav overlaying it).
const NO_NAV_PATTERNS = [
  /^\/$/,
  /^\/feed$/,
  /^\/feed\/new$/,
  /^\/feed\/u\//,
  /^\/coach$/,
  /^\/improve\/motivate$/,
];

function ShellWithNav({ children }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const showNav = user && !NO_NAV_PATTERNS.some((r) => r.test(pathname));
  return (
    <Shell hasNav={!!showNav}>
      {children}
      {showNav && <BottomNav />}
    </Shell>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <OutfitDraftProvider>
            <ToastProvider>
              <BrowserRouter>
              <RouteChangeTracker />
              <ShellWithNav>
                <Routes>
                  <Route path="/" element={<PublicOnly><WelcomeScreen /></PublicOnly>} />
                  <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordScreen /></PublicOnly>} />
                  {/* Reset & verify links open on the same host for web, iOS (WebView) and Android. */}
                  <Route path="/reset-password" element={<ResetPasswordScreen />} />
                  <Route path="/verify-email" element={<VerifyEmailScreen />} />
                  <Route path="/verify-email-change" element={<VerifyEmailChangeScreen />} />
                  <Route path="/account/edit" element={<Gated><EditAccountScreen /></Gated>} />
                  {/* Private read-only admin dashboard. Auth-required but
                      NOT onboarding-required — the admin doesn't need a
                      completed profile to view launch stats. Component
                      itself blocks non-admins. */}
                  <Route path="/admin/users" element={<Protected><AdminUsersScreen /></Protected>} />
                  <Route path="/onboarding" element={<Gated><OnboardingScreen /></Gated>} />
                  <Route path="/home" element={<Gated><HomeScreen /></Gated>} />
                  <Route path="/upload" element={<Gated><UploadScreen /></Gated>} />
                  <Route path="/style-pick" element={<Gated><StyleScreen /></Gated>} />
                  <Route path="/stylist" element={<Gated><StylistLandingScreen /></Gated>} />
                  <Route path="/style" element={<Gated><StyleHubScreen /></Gated>} />
                  <Route path="/improve" element={<Gated><ImproveScreen /></Gated>} />
                  <Route path="/improve/fitness"   element={<Gated><FitnessCoachScreen /></Gated>} />
                  <Route path="/improve/body-goal" element={<Gated><BodyGoalCoachScreen /></Gated>} />
                  <Route path="/improve/skincare"  element={<Gated><SkincareCoachScreen /></Gated>} />
                  <Route path="/improve/fragrance" element={<Gated><FragranceCoachScreen /></Gated>} />
                  <Route path="/improve/grooming"  element={<Gated><GroomingHubScreen /></Gated>} />
                  <Route path="/improve/grooming/hair"  element={<Gated><HairCoachScreen /></Gated>} />
                  <Route path="/improve/grooming/beard" element={<Gated><BeardCoachScreen /></Gated>} />
                  <Route path="/improve/motivate"  element={<Gated><MotivationCoachScreen /></Gated>} />
                  {/* /profile is the Account hub (settings, premium, sign out). */}
                  <Route path="/profile" element={<Gated><AccountScreen /></Gated>} />
                  <Route path="/settings" element={<Gated><SettingsScreen /></Gated>} />
                  <Route path="/settings/reminders" element={<Gated><RemindersSettingsScreen /></Gated>} />
                  {/* Legal pages — publicly accessible (no auth required) */}
                  <Route path="/legal/privacy" element={<PrivacyPolicyScreen />} />
                  {/* Permanent public URL for App Store / Play Store submissions. */}
                  <Route path="/privacy-policy" element={<PrivacyPolicyScreen />} />
                  {/* Public Account Deletion — for Google Play Data Safety
                      "Delete account URL". Must be reachable without login. */}
                  <Route path="/delete-account" element={<DeleteAccountRequestScreen />} />
                  {/* Public Support — used as the App Store / Play Store
                      "Support URL". Must be reachable without login. */}
                  <Route path="/support" element={<SupportScreen />} />
                  <Route path="/legal/terms" element={<TermsScreen />} />
                  <Route path="/legal/community-guidelines" element={<CommunityGuidelinesScreen />} />
                  {/* Legacy outfit-step-3 profile screen, kept on a hidden path for back-compat. */}
                  <Route path="/outfit-step" element={<Gated><ProfileScreen /></Gated>} />
                  <Route path="/results" element={<Gated><ResultsScreen /></Gated>} />
                  <Route path="/saved" element={<Gated><SavedScreen /></Gated>} />
                  <Route path="/premium" element={<Gated><PremiumScreen /></Gated>} />
                  <Route path="/celebrity" element={<Gated><CelebrityScreen /></Gated>} />
                  <Route path="/budget" element={<Gated><BudgetScreen /></Gated>} />
                  <Route path="/rate" element={<Gated><RateMyOutfitScreen /></Gated>} />
                  <Route path="/find-similar" element={<Gated><FindSimilarScreen /></Gated>} />
                  <Route path="/appearance" element={<Gated><AppearanceScreen /></Gated>} />
                  <Route path="/profile-setup" element={<Gated><ProfileSetupScreen /></Gated>} />
                  <Route path="/feed" element={<Gated><FashionFeedScreen /></Gated>} />
                  <Route path="/feed/new" element={<Gated><FashionFeedNewScreen /></Gated>} />
                  <Route path="/feed/u/:userId" element={<Gated><CreatorProfileScreen /></Gated>} />
                  <Route path="/progress" element={<Gated><ProgressScreen /></Gated>} />
                  <Route path="/coach" element={<Gated><MotivationCoachScreen /></Gated>} />
                  <Route path="/coach-legacy" element={<Gated><CoachScreen /></Gated>} />
                  <Route path="/nutrition" element={<Navigate to="/nutrition/scan" replace />} />
                  <Route path="/nutrition/scan" element={<Gated><NutritionScannerScreen /></Gated>} />
                  <Route path="/nutrition/targets" element={<Gated><NutritionScreen /></Gated>} />
                  <Route path="/trends" element={<Gated><TrendsScreen /></Gated>} />
                  <Route path="/glow-up/analyze" element={<Gated><GlowUpAnalyzeScreen /></Gated>} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </ShellWithNav>
            </BrowserRouter>
          </ToastProvider>
        </OutfitDraftProvider>
      </ThemeProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
